import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { userService } from '../services/UserService';
import { User, UserRole } from '../models/User';
import { Users as UsersIcon, Shield, Eye, Upload, UserPlus, Trash2, MoreVertical, Edit, Briefcase, X, Mail, KeyRound } from 'lucide-react';
import { AddUserModal } from '../components/modals/AddUserModal';
import { InviteUserModal } from '../components/modals/InviteUserModal';
import { INVITE_ONBOARDING } from '../utils/featureFlags';
import { EditUserModal } from '../components/modals/EditUserModal';
import { ConfirmModal } from '../components/modals/ConfirmModal';

export const Users: React.FC = () => {
  const { user: currentUser, resetPassword } = useAuth();
  const { company } = useCompany();
  const [users, setUsers] = useState<User[]>([]);
  const [supervisors, setSupervisors] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [actionMenuUserId, setActionMenuUserId] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null);
  const [sendingInvite, setSendingInvite] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (actionMenuUserId && !(e.target as Element).closest('[data-action-menu]')) {
        setActionMenuUserId(null);
      }
    };

    if (actionMenuUserId) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [actionMenuUserId]);

  const loadUsers = async () => {
    if (!company) {
      setLoading(false);
      return;
    }

    try {
      const companyUsers = await userService.getUsersByCompany(company.id);
      setUsers(companyUsers);

      // Load supervisors for the dropdown
      const companySupervisors = await userService.getSupervisorsByCompany(company.id);
      setSupervisors(companySupervisors);

      setError(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (userData: {
    email: string;
    name: string;
    role: any;
    supervisorId?: string;
    credentials?: string;
    specializations?: string[];
  }) => {
    if (!company) {
      throw new Error('No company selected');
    }

    await userService.inviteUser(company.id, userData);

    setSuccessMessage(`${userData.name} has been added. We emailed them a link to set their password.`);
    await loadUsers();
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleStatusChange = async (userId: string, newStatus: 'active' | 'inactive' | 'suspended') => {
    if (userId === currentUser?.id) {
      setError('You cannot change your own status');
      return;
    }

    setUpdatingStatus(userId);
    try {
      await userService.setUserStatus(userId, newStatus);
      setSuccessMessage(`User status updated to ${newStatus}`);
      await loadUsers();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating user status:', err);
      setError('Failed to update user status');
    } finally {
      setUpdatingStatus(null);
      setActionMenuUserId(null);
    }
  };

  const handleDeleteClick = (userId: string, userName: string) => {
    if (userId === currentUser?.id) {
      setError('You cannot delete your own account');
      return;
    }

    setUserToDelete({ id: userId, name: userName });
    setShowDeleteConfirm(true);
    setActionMenuUserId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setDeletingUser(userToDelete.id);
    try {
      await userService.deleteUser(userToDelete.id);
      setSuccessMessage(`User ${userToDelete.name} has been deleted`);
      await loadUsers();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user');
    } finally {
      setDeletingUser(null);
      setUserToDelete(null);
    }
  };

  const handleSendInvite = async (userEmail: string, userName: string) => {
    setSendingInvite(userEmail);
    try {
      await resetPassword(userEmail);
      setSuccessMessage(`Invite sent to ${userName}! They will receive an email to set their password.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error sending invite:', err);
      setError('Failed to send invite email. Please try again.');
    } finally {
      setSendingInvite(null);
      setActionMenuUserId(null);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowEditUserModal(true);
    setActionMenuUserId(null);
  };

  const handleUpdateUser = async (userId: string, userData: {
    name: string;
    role: UserRole;
    supervisorId?: string;
    badgeNumber?: string;
    department?: string;
    credentials?: string;
    specializations?: string[];
  }) => {
    try {
      // Filter out undefined values - Firestore doesn't accept undefined
      const cleanedData: Record<string, any> = {};
      Object.entries(userData).forEach(([key, value]) => {
        if (value !== undefined) {
          cleanedData[key] = value;
        }
      });

      await userService.updateUser(userId, cleanedData);
      setSuccessMessage('User updated successfully');
      await loadUsers();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating user:', err);
      throw err;
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return 'badge-primary';
      case 'supervisor':
        return 'badge-secondary';
      case 'rbt':
        return 'badge-success';
      case 'viewer':
        return 'badge-warning';
      default:
        return '';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Shield size={16} />;
      case 'supervisor':
        return <UsersIcon size={16} />;
      case 'rbt':
        return <Upload size={16} />;
      case 'viewer':
        return <Eye size={16} />;
      default:
        return null;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'badge-success';
      case 'inactive':
        return 'badge-warning';
      case 'suspended':
        return 'badge-error';
      default:
        return '';
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSupervisorName = (supervisorId: string | undefined) => {
    if (!supervisorId) return '—';
    const supervisor = users.find(u => u.id === supervisorId);
    return supervisor ? supervisor.name : 'Unknown';
  };

  if (currentUser?.role !== 'admin' && currentUser?.role !== 'supervisor') {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)' }}>
        <div className="alert alert-error">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)', textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-block',
              width: '48px',
              height: '48px',
              border: '4px solid var(--color-gray-200)',
              borderTop: '4px solid var(--color-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ marginTop: 'var(--spacing-4)', color: 'var(--color-gray-600)' }}>
            Loading users...
          </p>
        </div>
    );
  }

  return (
    <>
      <div className="container" style={{ paddingTop: 'var(--spacing-8)', paddingBottom: 'var(--spacing-8)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-8)' }}>
          <div>
            <h1 style={{ marginBottom: 'var(--spacing-2)' }}>User Management</h1>
            <p style={{ color: 'var(--color-gray-600)' }}>
              {company?.name} - {users.length} {users.length === 1 ? 'user' : 'users'}
            </p>
          </div>
          {currentUser?.role === 'admin' && (
            <div style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
              {INVITE_ONBOARDING && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="btn btn-ghost"
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}
                >
                  <KeyRound size={20} />
                  Invite
                </button>
              )}
              <button
                onClick={() => setShowAddUserModal(true)}
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}
              >
                <UserPlus size={20} />
                Add User
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-6)' }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success" style={{ marginBottom: 'var(--spacing-6)' }}>
            {successMessage}
          </div>
        )}

        {/* User Statistics */}
        <div className="grid grid-cols-4" style={{ marginBottom: 'var(--spacing-8)', gap: 'var(--spacing-4)' }}>
          <div className="card" style={{ padding: 'var(--spacing-4)' }}>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-white)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                <Shield size={20} />
              </div>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)' }}>Admins</p>
              <p style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-bold)' }}>
                {users.filter(u => u.role === 'admin').length}
              </p>
            </div>
          </div>

          <div className="card" style={{ padding: 'var(--spacing-4)' }}>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--color-secondary)',
                  color: 'var(--color-white)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                <UsersIcon size={20} />
              </div>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)' }}>Supervisors</p>
              <p style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-bold)' }}>
                {users.filter(u => u.role === 'supervisor').length}
              </p>
            </div>
          </div>

          <div className="card" style={{ padding: 'var(--spacing-4)' }}>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--color-success)',
                  color: 'var(--color-white)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                <Upload size={20} />
              </div>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)' }}>RBTs</p>
              <p style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-bold)' }}>
                {users.filter(u => u.role === 'rbt').length}
              </p>
            </div>
          </div>

          <div className="card" style={{ padding: 'var(--spacing-4)' }}>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--color-info)',
                  color: 'var(--color-white)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                <Briefcase size={20} />
              </div>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)' }}>HR</p>
              <p style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-bold)' }}>
                {users.filter(u => u.role === 'hr').length}
              </p>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">All Users</h2>
          </div>

          {users.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-8)', color: 'var(--color-gray-500)' }}>
              <UsersIcon size={48} style={{ margin: '0 auto var(--spacing-4)' }} />
              <p>No users found</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Supervisor</th>
                    <th>Badge Number</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Last Login</th>
                    {currentUser?.role === 'admin' && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div style={{ fontWeight: 'var(--font-medium)' }}>
                          {user.name}
                          {user.id === currentUser?.id && (
                            <span
                              style={{
                                marginLeft: 'var(--spacing-2)',
                                fontSize: 'var(--text-xs)',
                                color: 'var(--color-gray-500)',
                              }}
                            >
                              (You)
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                          <span style={{ marginRight: 'var(--spacing-1)' }}>
                            {getRoleIcon(user.role)}
                          </span>
                          {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                        </span>
                      </td>
                      <td>
                        {(user.role === 'rbt' || user.role === 'supervisor') ? (
                          <span style={{ color: user.supervisorId ? 'inherit' : 'var(--color-gray-400)' }}>
                            {getSupervisorName(user.supervisorId)}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-gray-400)' }}>—</span>
                        )}
                      </td>
                      <td>{user.badgeNumber || 'N/A'}</td>
                      <td>{user.department || 'N/A'}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(user.status || 'active')}`}>
                          {(user.status || 'active').charAt(0).toUpperCase() + (user.status || 'active').slice(1)}
                        </span>
                      </td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>{formatDate(user.lastLogin)}</td>
                      {currentUser?.role === 'admin' && (
                        <td>
                          {user.id !== currentUser?.id ? (
                            <div style={{ position: 'relative' }} data-action-menu>
                              <button
                                onClick={() => setActionMenuUserId(actionMenuUserId === user.id ? null : user.id)}
                                style={{
                                  background: 'none',
                                  border: '1px solid var(--color-gray-300)',
                                  borderRadius: 'var(--radius-md)',
                                  padding: 'var(--spacing-1) var(--spacing-2)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 'var(--spacing-1)',
                                }}
                                disabled={updatingStatus === user.id || deletingUser === user.id}
                              >
                                <MoreVertical size={16} />
                              </button>

                              {actionMenuUserId === user.id && (
                                <>
                                  {/* Backdrop */}
                                  <div
                                    onClick={() => setActionMenuUserId(null)}
                                    style={{
                                      position: 'fixed',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                      zIndex: 1999,
                                    }}
                                  />
                                  {/* Action Menu Modal */}
                                  <div
                                    style={{
                                      position: 'fixed',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      backgroundColor: 'white',
                                      border: '2px solid var(--color-gray-300)',
                                      borderRadius: 'var(--radius-lg)',
                                      boxShadow: '0 10px 50px rgba(0, 0, 0, 0.3)',
                                      zIndex: 2000,
                                      minWidth: '320px',
                                      padding: 'var(--spacing-4)',
                                    }}
                                  >
                                    {/* Header with close button */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
                                      <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', margin: 0 }}>
                                        Manage User: {user.name}
                                      </h3>
                                      <button
                                        onClick={() => setActionMenuUserId(null)}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          color: 'var(--color-gray-400)',
                                          padding: 'var(--spacing-1)',
                                        }}
                                      >
                                        <X size={20} />
                                      </button>
                                    </div>

                                    <button
                                    onClick={() => handleEditUser(user)}
                                    style={{
                                      width: '100%',
                                      padding: 'var(--spacing-3)',
                                      background: 'var(--color-gray-50)',
                                      border: 'none',
                                      borderRadius: 'var(--radius-md)',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 'var(--spacing-2)',
                                      color: 'var(--color-gray-700)',
                                      fontSize: 'var(--text-base)',
                                      fontWeight: 'var(--font-medium)',
                                      marginBottom: 'var(--spacing-2)',
                                    }}
                                  >
                                    <Edit size={18} />
                                    Edit User
                                  </button>
                                  <button
                                    onClick={() => handleSendInvite(user.email, user.name)}
                                    style={{
                                      width: '100%',
                                      padding: 'var(--spacing-3)',
                                      background: 'var(--color-info-light, #DBEAFE)',
                                      border: 'none',
                                      borderRadius: 'var(--radius-md)',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 'var(--spacing-2)',
                                      color: 'var(--color-info, #4F6367)',
                                      fontSize: 'var(--text-base)',
                                      fontWeight: 'var(--font-medium)',
                                      marginBottom: 'var(--spacing-2)',
                                    }}
                                    disabled={sendingInvite === user.email}
                                  >
                                    <Mail size={18} />
                                    {sendingInvite === user.email ? 'Sending...' : 'Send Invite'}
                                  </button>
                                  <div style={{ padding: 'var(--spacing-2)', backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--spacing-2)' }}>
                                    <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-2)', fontWeight: 'var(--font-medium)' }}>
                                      Change Status
                                    </p>
                                    <select
                                      value={user.status || 'active'}
                                      onChange={(e) => handleStatusChange(user.id, e.target.value as 'active' | 'inactive' | 'suspended')}
                                      className="form-input"
                                      style={{ fontSize: 'var(--text-base)', padding: 'var(--spacing-2)' }}
                                      disabled={updatingStatus === user.id}
                                    >
                                      <option value="active">Active</option>
                                      <option value="inactive">Inactive</option>
                                      <option value="suspended">Suspended</option>
                                    </select>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteClick(user.id, user.name)}
                                    style={{
                                      width: '100%',
                                      padding: 'var(--spacing-3)',
                                      background: 'var(--color-error-light, #FEE2E2)',
                                      border: 'none',
                                      borderRadius: 'var(--radius-md)',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 'var(--spacing-2)',
                                      color: 'var(--color-error)',
                                      fontSize: 'var(--text-base)',
                                      fontWeight: 'var(--font-medium)',
                                    }}
                                    disabled={deletingUser === user.id}
                                  >
                                    <Trash2 size={18} />
                                    {deletingUser === user.id ? 'Deleting...' : 'Delete User'}
                                  </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)' }}>-</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* User Limit Information */}
        <div className="card" style={{ marginTop: 'var(--spacing-6)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                User Capacity
              </h3>
              <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--text-sm)' }}>
                {users.length} of {company?.subscription?.userLimit || 0} users
              </p>
            </div>
            <div style={{ width: '300px' }}>
              <div
                style={{
                  width: '100%',
                  height: '12px',
                  backgroundColor: 'var(--color-gray-200)',
                  borderRadius: 'var(--radius-lg)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.min((users.length / (company?.subscription?.userLimit || 1)) * 100, 100)}%`,
                    height: '100%',
                    backgroundColor:
                      users.length >= (company?.subscription?.userLimit || 0)
                        ? 'var(--color-error)'
                        : users.length >= (company?.subscription?.userLimit || 0) * 0.8
                        ? 'var(--color-warning)'
                        : 'var(--color-success)',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onSubmit={handleAddUser}
        supervisors={supervisors}
      />

      {INVITE_ONBOARDING && (
        <InviteUserModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} />
      )}

      <EditUserModal
        isOpen={showEditUserModal}
        onClose={() => {
          setShowEditUserModal(false);
          setEditingUser(null);
        }}
        onSubmit={handleUpdateUser}
        user={editingUser}
        supervisors={supervisors}
      />

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message={`Are you sure you want to delete user "${userToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </>
  );
};
